import { Component, ContentChild, ElementRef, HostListener, inject, Input, OnDestroy } from '@angular/core';
import { AbstractComponent } from '../../abstract.component';
import { SolTooltip } from '../../../models/core/sol-tooltip';
import { TooltipService } from '../../../services/tooltip.service';
import { SolTooltipState } from './sol-tooltip.state';
import { SolTooltipContentDirective } from './sol-tooltip-content.directive';

let tooltipCounter = 0;

@Component({
  selector: 'sol-tooltip',
  imports: [],
  template: '<ng-content />',
  styles: [
    `
      :host {
        display: inline-flex;
        min-width: 0;
      }
    `,
  ],
})
export class SolTooltipComponent extends AbstractComponent<SolTooltipState> implements OnDestroy {
  // Dependencies
  private readonly tooltipService = inject(TooltipService);
  private readonly elementRef = inject(ElementRef);

  // Content
  @ContentChild(SolTooltipContentDirective)
  private content?: SolTooltipContentDirective;

  // Internal
  private readonly tooltipId = 'tooltip-' + tooltipCounter++;
  private hoverTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    super(SolTooltipState.of());
  }

  @Input()
  public set tooltip(tooltip: SolTooltip) {
    this.update(state => state.withTooltip(tooltip));
  }

  @HostListener('mouseenter')
  protected onMouseEnter(): void {
    this.clearTimer();
    this.hoverTimer = setTimeout(() => this.showTooltip(), 500);
  }

  @HostListener('mouseleave')
  protected onMouseLeave(): void {
    this.hideTooltip();
  }

  @HostListener('focusin')
  protected onFocusIn(): void {
    this.clearTimer();
    this.showTooltip();
  }

  @HostListener('focusout')
  protected onFocusOut(): void {
    this.hideTooltip();
  }

  public ngOnDestroy(): void {
    this.hideTooltip();
  }

  private showTooltip(): void {
    const tooltip = this.state.tooltip;
    const template = this.content?.template;
    if (tooltip.custom) {
      if (!template) {
        return;
      }
    } else if (tooltip.text.isEmpty()) {
      return;
    }
    const bounds = this.elementRef.nativeElement.getBoundingClientRect();
    this.tooltipService.show({
      id: this.tooltipId,
      text: tooltip.text,
      position: tooltip.position,
      triggerBounds: bounds,
      template: tooltip.custom ? template : undefined,
      custom: tooltip.custom,
      wide: tooltip.wide,
    });
  }

  private hideTooltip(): void {
    this.clearTimer();
    this.tooltipService.hide(this.tooltipId);
  }

  private clearTimer(): void {
    if (this.hoverTimer !== undefined) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = undefined;
    }
  }
}
